import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Fingerprint,
  FileSpreadsheet,
  Users,
  Upload,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Plus,
  X,
  ListChecks,
  Save,
  RefreshCw,
  Settings as SettingsIcon,
  Calculator,
  ArrowRight,
  LayoutGrid,
  Pencil,
} from "lucide-react";

const SOURCE_LABEL = {
  biometric: { lbl: "Biometric", color: "bg-sky-100 text-sky-700", icon: Fingerprint },
  manual: { lbl: "Manual CSV", color: "bg-amber-100 text-amber-700", icon: FileSpreadsheet },
  both: { lbl: "Both", color: "bg-violet-100 text-violet-700", icon: ListChecks },
};

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

import { useAuth } from "@/context/AuthContext";

export default function AttendanceV2() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [units, setUnits] = useState([]);
  const [runs, setRuns] = useState([]);
  const [activeRun, setActiveRun] = useState(null);
  const [showSourceMgr, setShowSourceMgr] = useState(false);
  const [showNewRun, setShowNewRun] = useState(false);
  const [newMonth, setNewMonth] = useState(thisMonth());
  const [newUnit, setNewUnit] = useState("");

  const load = async () => {
    try {
      const [u, r] = await Promise.all([
        api.get("/units"),
        api.get("/attendance-v2/runs"),
      ]);
      setUnits(u.data || []);
      setRuns(r.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { load(); }, []);

  const createRun = async () => {
    if (!newUnit) {
      toast.warning("Unit select karein");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("month", newMonth);
      fd.append("unit_id", newUnit);
      const { data } = await api.post("/attendance-v2/runs", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Run created");
      setShowNewRun(false);
      setActiveRun(data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="attendance-v2">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            Attendance v2 <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-bravura-100 text-bravura-700 font-bold align-middle">BETA — Hybrid</span>
          </h1>
          <p className="text-xs text-slate-500">Biometric live data (Mantra device) + Manual CSV for helpers. Once-a-day biometric refresh. Purana flow as-is.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowSourceMgr(true)}
            data-testid="att-v2-source-mgr-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 text-xs font-semibold"
          >
            <SettingsIcon className="h-3.5 w-3.5" /> Manage Sources
          </button>
          <button
            onClick={() => setShowNewRun(true)}
            data-testid="att-v2-new-run-btn"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-bravura text-white text-xs font-semibold"
          >
            <Plus className="h-3.5 w-3.5" /> New Run
          </button>
        </div>
      </div>

      {/* Runs list */}
      <div className="rounded-xl bg-white ring-1 ring-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[11px] uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Month</th>
              <th className="text-left px-3 py-2">Unit</th>
              <th className="text-center px-3 py-2">Biometric</th>
              <th className="text-center px-3 py-2">Manual CSV</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 && (
              <tr><td colSpan={6} className="text-center text-xs text-slate-400 py-8">
                No v2 runs yet. Click "New Run" to start.
              </td></tr>
            )}
            {runs.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="px-3 py-2 font-semibold">{r.month}</td>
                <td className="px-3 py-2">{r.unit_name}</td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.biometric_uploaded ? (
                    <span className="text-emerald-700">✓ {r.biometric_matched} matched, {r.biometric_unmatched} unmatched</span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center text-xs">
                  {r.manual_uploaded ? (
                    <span className="text-emerald-700">✓ {r.manual_rows} rows</span>
                  ) : <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${
                    r.status === "committed" ? "bg-emerald-100 text-emerald-700" :
                    r.status === "review" ? "bg-amber-100 text-amber-700" :
                    "bg-slate-100 text-slate-600"
                  }`}>
                    {r.status?.toUpperCase()}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => setActiveRun(r)}
                    data-testid={`open-run-${r.id}`}
                    className="text-bravura text-xs font-bold"
                  >Open →</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNewRun && (
        <Modal title="New Attendance Run" onClose={() => setShowNewRun(false)}>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Month</label>
            <input
              type="month"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={newMonth}
              onChange={(e) => setNewMonth(e.target.value)}
              data-testid="new-run-month"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold block mb-1">Unit</label>
            <select
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
              data-testid="new-run-unit"
            >
              <option value="">Select unit…</option>
              {units.map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <button onClick={createRun} data-testid="create-run-btn" className="w-full rounded-lg bg-bravura text-white py-2.5 text-sm font-bold">
            Create Run
          </button>
        </Modal>
      )}

      {showSourceMgr && (
        <SourceManager units={units} onClose={() => setShowSourceMgr(false)} />
      )}

      {activeRun && (
        <RunDetail run={activeRun} onClose={() => { setActiveRun(null); load(); }} onChange={load} isSuperAdmin={isSuperAdmin} />
      )}
    </div>
  );
}

// ===========================================================================
// Source Manager — bulk-set per-employee source
// ===========================================================================
function SourceManager({ units, onClose }) {
  const [unitId, setUnitId] = useState("");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const load = async (uid) => {
    setLoading(true);
    try {
      const { data } = await api.get("/attendance-v2/source/summary",
        { params: { unit_id: uid || undefined } });
      setData(data);
      setSelected(new Set());
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(unitId); }, [unitId]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (newSource) => {
    if (selected.size === 0) {
      toast.warning("Select at least one employee");
      return;
    }
    try {
      await api.post("/attendance-v2/source/bulk", {
        employee_ids: Array.from(selected),
        attendance_source: newSource,
      });
      toast.success(`${selected.size} employees → ${newSource}`);
      load(unitId);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const employees = (data?.employees || []).filter((e) => !unitId || e.unit_id === unitId);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-base font-bold">Manage Attendance Source</h3>
            <p className="text-[11px] text-slate-500">Mark employees as Biometric (default), Manual CSV, or Both</p>
          </div>
          <button onClick={onClose}><X className="h-5 w-5" /></button>
        </div>

        <div className="p-4 border-b bg-slate-50">
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={unitId}
              onChange={(e) => setUnitId(e.target.value)}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
              data-testid="src-mgr-unit"
            >
              <option value="">All units</option>
              {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            {data?.counts && (
              <div className="text-xs flex gap-3">
                <span>📊 Total: <b>{data.total}</b></span>
                <span className="text-sky-700">⌬ Biometric: <b>{data.counts.biometric || 0}</b></span>
                <span className="text-amber-700">📄 Manual: <b>{data.counts.manual || 0}</b></span>
                <span className="text-violet-700">⊕ Both: <b>{data.counts.both || 0}</b></span>
              </div>
            )}
          </div>
          {selected.size > 0 && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold">{selected.size} selected →</span>
              <button onClick={() => apply("biometric")} data-testid="apply-biometric" className="px-2.5 py-1 rounded-md bg-sky-600 text-white text-xs font-semibold">Mark Biometric</button>
              <button onClick={() => apply("manual")} data-testid="apply-manual" className="px-2.5 py-1 rounded-md bg-amber-600 text-white text-xs font-semibold">Mark Manual</button>
              <button onClick={() => apply("both")} data-testid="apply-both" className="px-2.5 py-1 rounded-md bg-violet-600 text-white text-xs font-semibold">Mark Both</button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="text-left px-2 py-1">
                  <input
                    type="checkbox"
                    checked={employees.length > 0 && selected.size === employees.length}
                    onChange={(e) => setSelected(e.target.checked ? new Set(employees.map((x) => x.id)) : new Set())}
                  />
                </th>
                <th className="text-left px-2 py-1">Code</th>
                <th className="text-left px-2 py-1">Name</th>
                <th className="text-left px-2 py-1">Punch ID</th>
                <th className="text-left px-2 py-1">Source</th>
              </tr>
            </thead>
            <tbody>
              {employees.map((e) => {
                const src = e.attendance_source || "biometric";
                const meta = SOURCE_LABEL[src];
                return (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={selected.has(e.id)}
                        onChange={(ev) => {
                          const s = new Set(selected);
                          if (ev.target.checked) s.add(e.id); else s.delete(e.id);
                          setSelected(s);
                        }}
                        data-testid={`src-cb-${e.id}`}
                      />
                    </td>
                    <td className="px-2 py-1.5 font-mono text-xs">{e.employee_code}</td>
                    <td className="px-2 py-1.5">{e.name}</td>
                    <td className="px-2 py-1.5 text-xs text-slate-500">{e.punch_id || "—"}</td>
                    <td className="px-2 py-1.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-semibold ${meta.color}`}>
                        <meta.icon className="h-3 w-3" />
                        {meta.lbl}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Run Detail — upload bio + manual, anomalies, commit
// ===========================================================================
function RunDetail({ run: initialRun, onClose, onChange, isSuperAdmin }) {
  const [run, setRun] = useState(initialRun);
  const [anomalies, setAnomalies] = useState([]);
  const [counts, setCounts] = useState({ total: 0, blocking_unresolved: 0, warning_unresolved: 0, resolved: 0, by_type: {} });
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("unresolved");
  const [filterDate, setFilterDate] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("uploads");
  const [resolveModal, setResolveModal] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [payrollPreviewOpen, setPayrollPreviewOpen] = useState(false);
  const [preparingPayroll, setPreparingPayroll] = useState(false);

  const reload = async () => {
    try {
      const params = {};
      if (filterType !== "all") params.anomaly_type = filterType;
      if (filterStatus === "resolved") params.only_resolved = true;
      if (filterStatus === "unresolved") params.only_unresolved = true;
      if (filterDate) params.date = filterDate;
      const [r, a] = await Promise.all([
        api.get(`/attendance-v2/runs/${run.id}`),
        api.get(`/attendance-v2/runs/${run.id}/anomalies`, { params }),
      ]);
      setRun(r.data);
      // Support both new {rows, counts} and legacy [] shapes
      const body = a.data;
      if (Array.isArray(body)) {
        setAnomalies(body);
        setCounts({ total: body.length, blocking_unresolved: body.filter(x => !x.resolved && x.anomaly_blocking).length, warning_unresolved: body.filter(x => !x.resolved && !x.anomaly_blocking).length, resolved: body.filter(x => x.resolved).length, by_type: {} });
      } else {
        setAnomalies(body.rows || []);
        setCounts(body.counts || { total: 0, blocking_unresolved: 0, warning_unresolved: 0, resolved: 0, by_type: {} });
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  useEffect(() => { reload(); }, [run.id, filterType, filterStatus, filterDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/attendance-v2/runs/${run.id}/upload-${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${kind} CSV uploaded`);
      reload();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const refreshBio = async (force = false) => {
    try {
      await api.post(`/attendance-v2/runs/${run.id}/refresh-biometric`, null, {
        params: force ? { force: true } : {},
      });
      toast.success("Biometric data refreshed from live punches");
      reload();
      onChange();
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiErrorDetail(e.response?.data?.detail);
      if (status === 429) {
        if (window.confirm(`${detail}\n\nRe-pull anyway?`)) {
          await refreshBio(true);
        }
      } else {
        toast.error(detail);
      }
    }
  };

  const commit = async () => {
    if (!window.confirm("Commit this run? Once committed, edits require super_admin.")) return;
    try {
      await api.post(`/attendance-v2/runs/${run.id}/commit`);
      toast.success("Committed");
      reload();
      onChange();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const preparePayroll = async () => {
    if (!window.confirm(
      `Prepare payroll for month ${run.month}?\n\n` +
      `Yeh V2 ke daily attendance ko aggregate karke ` +
      `attendance_monthly_summary mein push karega. ` +
      `Phir aap Payroll → Run Monthly se payslips generate kar sakte ho.`
    )) return;
    setPreparingPayroll(true);
    try {
      const { data } = await api.post(`/attendance-v2/runs/${run.id}/prepare-payroll`);
      toast.success(
        `Done — ${data.summaries_written} employees pushed. Now run payroll for ${data.month}.`,
      );
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setPreparingPayroll(false);
    }
  };

  const del = async () => {
    const isCommitted = run.status === "committed";
    const msg = isCommitted
      ? "⚠️ Delete a COMMITTED run? This will permanently remove the run, all daily rows, and anomalies. Cannot be undone."
      : "Delete this run? All v2 data will be removed.";
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/attendance-v2/runs/${run.id}`);
      toast.success("Deleted");
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const unresolvedBlockers = counts.blocking_unresolved;

  const filteredAnomalies = (anomalies || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (
      String(r.employee_name || "").toLowerCase().includes(q) ||
      String(r.employee_code || "").toLowerCase().includes(q) ||
      String(r.punch_id || "").toLowerCase().includes(q)
    );
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <h3 className="text-base font-bold">{run.unit_name} — {run.month}</h3>
            <p className="text-[11px] text-slate-500">Status: <b>{run.status?.toUpperCase()}</b></p>
          </div>
          <div className="flex items-center gap-2">
            {(run.status !== "committed" || isSuperAdmin) && (
              <button
                onClick={del}
                className={`text-red-600 hover:text-red-700 inline-flex items-center gap-1 ${run.status === "committed" ? "ring-1 ring-red-300 px-2 py-1 rounded text-xs font-semibold" : ""}`}
                data-testid="run-delete"
                title={run.status === "committed" ? "Super-admin only · permanent" : "Delete run"}
              >
                <Trash2 className="h-4 w-4" />
                {run.status === "committed" && <span>Delete committed</span>}
              </button>
            )}
            <button onClick={onClose}><X className="h-5 w-5" /></button>
          </div>
        </div>

        <div className="border-b bg-slate-50 px-4">
          <div className="flex gap-1 py-2">
            {[
              { v: "uploads", label: "Uploads" },
              { v: "anomalies", label: `Anomalies (${counts.blocking_unresolved + counts.warning_unresolved})` },
              { v: "grid", label: "Raw Grid" },
              { v: "summary", label: "Summary" },
            ].map((t) => (
              <button
                key={t.v}
                onClick={() => setTab(t.v)}
                data-testid={`run-tab-${t.v}`}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold ${
                  tab === t.v ? "bg-white text-slate-900 shadow" : "text-slate-500"
                }`}
              >{t.label}</button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {tab === "uploads" && (
            <div className="grid md:grid-cols-2 gap-4">
              <BiometricRefreshCard
                run={run}
                onRefresh={() => refreshBio(false)}
                disabled={run.status === "committed"}
              />
              <UploadCard
                title="Manual Hours CSV"
                desc="For helpers / casuals not on biometric. Columns: employee_code, date, worked_hours."
                Icon={FileSpreadsheet}
                color="amber"
                uploaded={run.manual_uploaded}
                meta={run.manual_uploaded ? `${run.manual_rows} rows accepted · ${run.manual_skipped || 0} skipped` : null}
                onUpload={(f) => upload("manual", f)}
                disabled={run.status === "committed"}
                testId="upload-manual"
              />
            </div>
          )}

          {tab === "anomalies" && (
            <AnomaliesTab
              counts={counts}
              rows={filteredAnomalies}
              filterType={filterType} setFilterType={setFilterType}
              filterStatus={filterStatus} setFilterStatus={setFilterStatus}
              filterDate={filterDate} setFilterDate={setFilterDate}
              search={search} setSearch={setSearch}
              onCorrect={(row) => setResolveModal(row)}
              disabled={run.status === "committed"}
              selected={selected}
              setSelected={setSelected}
              onBulkFix={() => setBulkOpen(true)}
            />
          )}

          {tab === "grid" && (
            <RawGridTab
              runId={run.id}
              status={run.status}
              isSuperAdmin={isSuperAdmin}
              onChanged={onChange}
            />
          )}

          {tab === "summary" && <RunSummary run={run} />}
        </div>

        <div className="border-t p-3 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-[11px] text-slate-500">
            {unresolvedBlockers > 0 && <span className="text-red-700 font-semibold">⚠ {unresolvedBlockers} blocking anomalies unresolved</span>}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPayrollPreviewOpen(true)}
              disabled={!run}
              data-testid="payroll-preview-btn"
              className="px-3 py-2 rounded-lg ring-1 ring-slate-300 text-sm font-semibold inline-flex items-center gap-1.5 hover:bg-slate-100"
            >
              <Calculator className="h-4 w-4" /> Payroll Preview
            </button>
            <button
              onClick={preparePayroll}
              disabled={run.status !== "committed" || preparingPayroll}
              data-testid="prepare-payroll-btn"
              title={run.status !== "committed" ? "Commit run first" : "Push V2 data into payroll engine"}
              className="px-3 py-2 rounded-lg bg-bravura text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <ArrowRight className="h-4 w-4" />
              {preparingPayroll ? "Preparing…" : "Prepare Payroll"}
            </button>
            <button
              onClick={commit}
              disabled={run.status === "committed" || unresolvedBlockers > 0}
              data-testid="commit-run-btn"
              className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              <Save className="h-4 w-4" /> {run.status === "committed" ? "Committed ✓" : "Commit Run"}
            </button>
          </div>
        </div>
      </div>

      {payrollPreviewOpen && (
        <PayrollPreviewDialog
          runId={run.id}
          month={run.month}
          onClose={() => setPayrollPreviewOpen(false)}
        />
      )}

      {resolveModal && (
        <ResolveAnomaly
          anomaly={resolveModal}
          onClose={() => setResolveModal(null)}
          onDone={() => { setResolveModal(null); reload(); }}
        />
      )}
      {bulkOpen && (
        <BulkFixDialog
          rows={(anomalies || []).filter(a => selected.has(a.id) && !a.resolved)}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSelected(new Set()); reload(); }}
        />
      )}
    </div>
  );
}

function UploadCard({ title, desc, Icon, color, uploaded, meta, onUpload, disabled, testId }) {
  const colorMap = {
    sky: "from-sky-50 ring-sky-200 text-sky-700",
    amber: "from-amber-50 ring-amber-200 text-amber-700",
  };
  return (
    <div className={`rounded-2xl ring-1 bg-gradient-to-br to-white p-4 ${colorMap[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`h-9 w-9 rounded-xl bg-white flex items-center justify-center`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900">{title}</div>
          <div className="text-[11px] text-slate-500">{desc}</div>
        </div>
      </div>
      {uploaded ? (
        <div className="rounded-lg bg-white ring-1 ring-emerald-200 p-2 text-[11px] text-emerald-700 mb-2">
          ✓ {meta}
        </div>
      ) : (
        <div className="text-[11px] text-slate-500 mb-2">Not uploaded yet</div>
      )}
      <label className={`block rounded-lg bg-white border-2 border-dashed border-slate-300 p-3 text-center cursor-pointer transition ${disabled ? "opacity-50 cursor-not-allowed" : "hover:border-bravura"}`}>
        <Upload className="h-5 w-5 mx-auto text-slate-400 mb-1" />
        <span className="text-[11px] font-semibold text-slate-700">
          {uploaded ? "Re-upload" : "Choose file"}
        </span>
        <input
          type="file"
          accept=".csv,.xls,.xlsx"
          onChange={(e) => { if (e.target.files?.[0]) onUpload(e.target.files[0]); }}
          disabled={disabled}
          className="hidden"
          data-testid={testId}
        />
      </label>
    </div>
  );
}

function BiometricRefreshCard({ run, onRefresh, disabled }) {
  const lastRefreshed = run.biometric_refreshed_at
    ? new Date(run.biometric_refreshed_at).toLocaleString("en-IN", {
        day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
      })
    : null;
  return (
    <div className="rounded-2xl ring-1 ring-sky-200 bg-gradient-to-br from-sky-50 to-white p-4 text-sky-700">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-9 w-9 rounded-xl bg-white flex items-center justify-center">
          <Fingerprint className="h-5 w-5" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900">Biometric (Live Data)</div>
          <div className="text-[11px] text-slate-500">
            Auto-pulled from Mantra device punches. No CSV upload needed.
          </div>
        </div>
      </div>
      {run.biometric_uploaded ? (
        <div className="rounded-lg bg-white ring-1 ring-emerald-200 p-2 text-[11px] text-emerald-700 mb-2">
          ✓ {run.biometric_employees_with_data || run.biometric_matched || 0} of{" "}
          {run.biometric_eligible_employees || (run.biometric_matched + run.biometric_unmatched) || 0}{" "}
          employees · {run.biometric_anomalies || 0} anomalies
          {lastRefreshed && <div className="text-slate-500 mt-0.5">Last refresh: {lastRefreshed}</div>}
        </div>
      ) : (
        <div className="text-[11px] text-slate-500 mb-2">Not refreshed yet</div>
      )}
      <button
        type="button"
        onClick={onRefresh}
        disabled={disabled}
        data-testid="refresh-biometric-btn"
        className={`w-full rounded-lg bg-sky-600 text-white py-2.5 text-xs font-bold flex items-center justify-center gap-1.5 ${
          disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-sky-700"
        }`}
      >
        <RefreshCw className="h-3.5 w-3.5" />
        {run.biometric_uploaded ? "Refresh from device" : "Pull biometric data"}
      </button>
      <div className="text-[10px] text-slate-400 mt-1.5 text-center">
        Auto-refresh runs daily at 11:30 PM IST · Force re-pull anytime
      </div>
    </div>
  );
}



const TYPE_LABELS = {
  MISSED_PUNCH_OUT: { short: "Missed Punch Out", color: "rose" },
  MISSED_PUNCH_IN: { short: "Missed Punch In", color: "rose" },
  SHORT_DAY: { short: "Short Day (<4h)", color: "amber" },
  OVER_16_HOURS: { short: "Over 16 hrs", color: "amber" },
};

const REASON_OPTIONS = [
  "Forgot to punch out — confirmed verbally",
  "Forgot to punch in — confirmed verbally",
  "Machine error — verified by supervisor",
  "Approved early leave",
];

function ddmmyyyy(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}-${m}-${y}`;
}
function dayName(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { weekday: "long" });
}

function AnomaliesTab({ counts, rows, filterType, setFilterType, filterStatus, setFilterStatus, filterDate, setFilterDate, search, setSearch, onCorrect, disabled, selected, setSelected, onBulkFix }) {
  const selectableRows = rows.filter(r => !(r.resolved || r.anomaly_resolved));
  const allSelected = selectableRows.length > 0 && selectableRows.every(r => selected.has(r.id));
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(selectableRows.map(r => r.id)));
    }
  };
  const c = counts;
  return (
    <div className="space-y-4" data-testid="v2-anomalies-tab">
      {/* Header banner */}
      {c.blocking_unresolved > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-800 flex items-center gap-3" data-testid="v2-blocking-banner">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <div>
            <div className="font-semibold">
              {c.blocking_unresolved} punch anomal{c.blocking_unresolved === 1 ? "y" : "ies"} need{c.blocking_unresolved === 1 ? "s" : ""} review before payroll can be processed.
            </div>
            <div className="text-xs text-red-700 mt-0.5">Correct the times below or mark days as Absent.</div>
          </div>
        </div>
      )}
      {c.blocking_unresolved === 0 && c.total > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 flex-shrink-0" />
          <div>All blocking anomalies resolved. {c.warning_unresolved} warning{c.warning_unresolved === 1 ? "" : "s"} can be reviewed (optional).</div>
        </div>
      )}
      {c.total === 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-8 text-center text-sm text-slate-500" data-testid="v2-no-anomalies">
          No anomalies found. Either no biometric refresh yet, or all punches are clean.
        </div>
      )}

      {/* Counter chips */}
      {c.total > 0 && (
        <div className="flex items-center gap-2 flex-wrap text-xs">
          <span className="px-2 py-1 rounded-md ring-1 bg-red-50 ring-red-200 text-red-800 font-semibold">
            {c.blocking_unresolved} blocker{c.blocking_unresolved === 1 ? "" : "s"}
          </span>
          <span className="px-2 py-1 rounded-md ring-1 bg-amber-50 ring-amber-200 text-amber-800 font-semibold">
            {c.warning_unresolved} warning{c.warning_unresolved === 1 ? "" : "s"}
          </span>
          <span className="px-2 py-1 rounded-md ring-1 bg-emerald-50 ring-emerald-200 text-emerald-800 font-semibold">
            {c.resolved} resolved
          </span>
          <span className="text-slate-400 ml-1">/</span>
          <span className="text-slate-500">{c.total} total</span>
        </div>
      )}

      {/* Filter bar */}
      {c.total > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Type</label>
            <select value={filterType} onChange={(e) => setFilterType(e.target.value)} data-testid="v2-filter-type" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              <option value="all">All types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v.short} ({c.by_type?.[k] || 0})</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Status</label>
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="v2-filter-status" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm">
              <option value="unresolved">Unresolved</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Date</label>
            <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} data-testid="v2-filter-date" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Search</label>
            <input placeholder="Code, name, or punch ID" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="v2-filter-search" className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          </div>
        </div>
      )}

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bg-bravura/10 border border-bravura ring-1 ring-bravura/30 rounded-lg p-3 flex items-center justify-between sticky top-0 z-10" data-testid="v2-bulk-bar">
          <div className="text-sm">
            <strong>{selected.size}</strong> {selected.size === 1 ? "anomaly" : "anomalies"} selected
            <span className="text-slate-500 text-xs ml-2">— typical use: morning power-cut, common arrival time</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 rounded-md text-xs text-slate-600 hover:bg-white"
              data-testid="v2-bulk-clear"
            >Clear</button>
            <button
              onClick={onBulkFix}
              disabled={disabled}
              data-testid="v2-bulk-fix-btn"
              className="px-4 py-1.5 rounded-md bg-bravura text-white text-xs font-bold hover:bg-bravura-700 disabled:opacity-50"
            >
              Bulk Fix Selected
            </button>
          </div>
        </div>
      )}

      {/* Anomalies table */}
      {c.total > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={disabled || selectableRows.length === 0}
                    data-testid="v2-bulk-select-all"
                    title="Select all unresolved"
                  />
                </th>
                <th className="text-left px-3 py-2 font-medium">Status</th>
                <th className="text-left px-3 py-2 font-medium">Employee</th>
                <th className="text-left px-3 py-2 font-medium">Date</th>
                <th className="text-left px-3 py-2 font-medium">Type</th>
                <th className="text-left px-3 py-2 font-medium">First IN</th>
                <th className="text-left px-3 py-2 font-medium">Last OUT</th>
                <th className="text-right px-3 py-2 font-medium">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-slate-400 text-xs">No anomalies match the current filters.</td></tr>
              )}
              {rows.map((r) => {
                const t = TYPE_LABELS[r.anomaly_type] || { short: r.anomaly_type, color: "slate" };
                const isResolved = !!(r.resolved || r.anomaly_resolved);
                const isBlocker = !!r.anomaly_blocking;
                const isChecked = selected.has(r.id);
                const badgeColor = {
                  rose: "bg-red-100 text-red-800",
                  amber: "bg-amber-100 text-amber-800",
                  slate: "bg-slate-100 text-slate-800",
                }[t.color] || "bg-slate-100 text-slate-800";
                return (
                  <tr key={r.id} data-testid={`v2-anomaly-row-${r.id}`} className={isResolved ? "bg-emerald-50/30" : isChecked ? "bg-bravura/5" : ""}>
                    <td className="px-3 py-2">
                      {!isResolved && (
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleOne(r.id)}
                          disabled={disabled}
                          data-testid={`v2-bulk-select-${r.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {isResolved ? (
                        <span className="text-emerald-700 text-xs flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" />resolved</span>
                      ) : isBlocker ? (
                        <span className="text-red-600 text-xs">● blocker</span>
                      ) : (
                        <span className="text-amber-600 text-xs">● warning</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{r.employee_name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{r.punch_id || "—"} · {r.employee_code}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className="font-medium">{ddmmyyyy(r.date)}</div>
                      <div className="text-[11px] text-slate-500">{dayName(r.date)}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      <span className={`inline-block px-1.5 py-0.5 rounded font-medium ${badgeColor}`}>{t.short}</span>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{r.first_in || r.original_first_in || <span className="text-red-500">missing</span>}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.last_out || r.original_last_out || <span className="text-red-500">missing</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => onCorrect(r)}
                        disabled={disabled && !isResolved}
                        data-testid={`v2-correct-btn-${r.id}`}
                        className={`px-3 py-1 rounded-md text-[11px] font-semibold ${
                          isResolved
                            ? "ring-1 ring-slate-300 text-slate-700 bg-white hover:bg-slate-50"
                            : "bg-bravura text-white hover:bg-bravura-700"
                        } ${disabled && !isResolved ? "opacity-50 cursor-not-allowed" : ""}`}
                      >
                        {isResolved ? "Re-edit" : "Correct"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResolveAnomaly({ anomaly, onClose, onDone }) {
  const [firstIn, setFirstIn] = useState(anomaly.first_in || anomaly.original_first_in || "");
  const [lastOut, setLastOut] = useState(anomaly.last_out || anomaly.original_last_out || "");
  const [reason, setReason] = useState(
    anomaly.anomaly_type === "MISSED_PUNCH_IN" ? REASON_OPTIONS[1] : REASON_OPTIONS[0]
  );
  const [reasonOther, setReasonOther] = useState("");
  const [busy, setBusy] = useState(false);

  const callResolve = async (markAbsent) => {
    const finalReason = reason === "Other" ? reasonOther.trim() : reason;
    if (!finalReason) {
      toast.error("Reason is required");
      return;
    }
    if (!markAbsent) {
      if (!/^\d{2}:\d{2}$/.test(firstIn) || !/^\d{2}:\d{2}$/.test(lastOut)) {
        toast.error("Both times must be in HH:MM 24-hour format");
        return;
      }
    }
    setBusy(true);
    try {
      await api.post(`/attendance-v2/anomalies/${anomaly.id}/resolve`, null, {
        params: {
          first_in: markAbsent ? undefined : firstIn,
          last_out: markAbsent ? undefined : lastOut,
          mark_absent: markAbsent,
          reason: finalReason,
        },
      });
      toast.success(markAbsent ? "Marked as Absent" : "Correction saved");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4" data-testid="v2-correct-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold">
            Correct punch — {anomaly.employee_name} · {ddmmyyyy(anomaly.date)}
          </h4>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs space-y-1">
          <div className="text-slate-500 uppercase tracking-wider text-[10px] font-semibold">Recorded by machine</div>
          <div>First IN: <span className="font-mono">{anomaly.original_first_in || anomaly.first_in || "—"}</span></div>
          <div>Last OUT: <span className="font-mono">{anomaly.original_last_out || anomaly.last_out || "—"}</span></div>
          <div className="text-red-700 mt-1">{anomaly.anomaly_reason}</div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">First IN</label>
            <input
              type="time"
              data-testid="v2-correct-first-in"
              value={firstIn}
              onChange={(e) => setFirstIn(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Last OUT</label>
            <input
              type="time"
              data-testid="v2-correct-last-out"
              value={lastOut}
              onChange={(e) => setLastOut(e.target.value)}
              className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Reason</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="v2-correct-reason"
            className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
          >
            {REASON_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            <option value="Other">Other (specify)</option>
          </select>
          {reason === "Other" && (
            <textarea
              rows={2}
              data-testid="v2-correct-reason-other"
              className="w-full mt-2 rounded-md border border-slate-300 px-2 py-2 text-sm"
              placeholder="Specify reason"
              value={reasonOther}
              onChange={(e) => setReasonOther(e.target.value)}
            />
          )}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
          <button
            onClick={() => callResolve(true)}
            disabled={busy}
            data-testid="v2-mark-absent-btn"
            className="px-4 py-2 rounded-md text-sm ring-1 ring-slate-300 text-slate-700 bg-white hover:bg-slate-50 disabled:opacity-50"
          >
            Mark as Absent
          </button>
          <button
            onClick={() => callResolve(false)}
            disabled={busy}
            data-testid="v2-save-correction-btn"
            className="px-4 py-2 rounded-md text-sm bg-bravura text-white hover:bg-bravura-700 font-semibold disabled:opacity-50"
          >
            Save correction
          </button>
        </div>
      </div>
    </div>
  );
}

function RunSummary({ run }) {
  const [daily, setDaily] = useState([]);
  useEffect(() => {
    api.get(`/attendance-v2/runs/${run.id}/daily`)
      .then(({ data }) => setDaily(data || []))
      .catch(() => {});
  }, [run.id]);
  const bySource = { biometric: 0, manual: 0 };
  for (const d of daily) bySource[d.source] = (bySource[d.source] || 0) + 1;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <SumCard label="Biometric rows" value={bySource.biometric} color="sky" />
        <SumCard label="Manual rows" value={bySource.manual} color="amber" />
        <SumCard label="Total rows" value={daily.length} color="violet" />
      </div>
      <p className="text-[11px] text-slate-500">Daily merged rows. Payroll v2 will read from this dataset (manual switch later).</p>
    </div>
  );
}

function SumCard({ label, value, color }) {
  const map = {
    sky: "bg-sky-50 ring-sky-200 text-sky-700",
    amber: "bg-amber-50 ring-amber-200 text-amber-700",
    violet: "bg-violet-50 ring-violet-200 text-violet-700",
  };
  return (
    <div className={`rounded-xl ring-1 p-3 ${map[color]}`}>
      <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Chivo" }}>{value}</div>
    </div>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl p-5 space-y-3 shadow-xl">
        <div className="flex items-center justify-between">
          <h4 className="text-base font-bold">{title}</h4>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BulkFixDialog({ rows, onClose, onDone }) {
  const [firstIn, setFirstIn] = useState("09:00");
  const [mode, setMode] = useState("shift");
  const [commonLastOut, setCommonLastOut] = useState("");
  const [reason, setReason] = useState("Morning power cut — biometric machine offline");
  const [busy, setBusy] = useState(false);

  const dates = Array.from(new Set(rows.map(r => r.date)));
  const sameDay = dates.length === 1;

  const submit = async () => {
    if (!reason.trim()) {
      toast.error("Reason is required");
      return;
    }
    if (mode !== "absent" && !/^\d{2}:\d{2}$/.test(firstIn)) {
      toast.error("First IN must be HH:MM");
      return;
    }
    if (mode === "common" && !/^\d{2}:\d{2}$/.test(commonLastOut)) {
      toast.error("Common Last OUT must be HH:MM");
      return;
    }
    setBusy(true);
    try {
      const body = {
        anomaly_ids: rows.map(r => r.id),
        reason: reason.trim(),
        mark_absent: mode === "absent",
        first_in: mode === "absent" ? null : firstIn,
        last_out: mode === "common" ? commonLastOut : null,
        use_original_as_last_out: mode === "shift",
      };
      const { data } = await api.post("/attendance-v2/anomalies/bulk-resolve", body);
      toast.success(`Resolved ${data.resolved} anomal${data.resolved === 1 ? "y" : "ies"}`);
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-slate-900/70 flex items-center justify-center p-4" data-testid="v2-bulk-dialog">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="p-5 border-b flex items-start justify-between">
          <div>
            <h4 className="text-base font-bold">Bulk Fix — {rows.length} anomalies</h4>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Use this for morning power-cut scenarios: many employees missed punch-in,
              their later evening punch was captured as First IN by mistake.
            </p>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {!sameDay && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-800">
              ⚠ Selected anomalies span <strong>{dates.length} different dates</strong>. Same First IN time will apply to all.
            </div>
          )}

          <div className="space-y-2">
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Mode</label>
            <div className="grid grid-cols-1 gap-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg ring-1 cursor-pointer ${mode === "shift" ? "ring-bravura bg-bravura/5" : "ring-slate-200 hover:bg-slate-50"}`} data-testid="v2-bulk-mode-shift">
                <input type="radio" checked={mode === "shift"} onChange={() => setMode("shift")} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Shift punch → Last OUT (recommended for morning issue)</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Each row's existing single punch becomes <strong>Last OUT</strong>. Common First IN set to value below. Each employee keeps their unique leave time.
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg ring-1 cursor-pointer ${mode === "common" ? "ring-bravura bg-bravura/5" : "ring-slate-200 hover:bg-slate-50"}`} data-testid="v2-bulk-mode-common">
                <input type="radio" checked={mode === "common"} onChange={() => setMode("common")} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-semibold">Set common First IN + Last OUT for all</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Override both times with the same value for every row. Use when the entire group had identical timing.
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg ring-1 cursor-pointer ${mode === "absent" ? "ring-red-300 bg-red-50" : "ring-slate-200 hover:bg-slate-50"}`} data-testid="v2-bulk-mode-absent">
                <input type="radio" checked={mode === "absent"} onChange={() => setMode("absent")} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-red-700">Mark all as Absent</div>
                  <div className="text-[11px] text-slate-600 mt-0.5">No times applied. Worked minutes = 0 for all selected rows.</div>
                </div>
              </label>
            </div>
          </div>

          {mode !== "absent" && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Common First IN</label>
                <input type="time" value={firstIn} onChange={(e) => setFirstIn(e.target.value)} data-testid="v2-bulk-first-in" className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
              </div>
              {mode === "common" && (
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Common Last OUT</label>
                  <input type="time" value={commonLastOut} onChange={(e) => setCommonLastOut(e.target.value)} data-testid="v2-bulk-last-out" className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" />
                </div>
              )}
            </div>
          )}

          <div>
            <label className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Reason</label>
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="v2-bulk-reason" className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm" placeholder="e.g. Morning power cut on 12-May-2026 — biometric offline" />
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Preview ({rows.length} rows)</div>
            <div className="max-h-40 overflow-y-auto">
              <table className="w-full text-[11px]">
                <thead className="text-slate-500"><tr>
                  <th className="text-left py-1">Employee</th>
                  <th className="text-left py-1">Date</th>
                  <th className="text-left py-1">Original</th>
                  <th className="text-left py-1">After Fix</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-100">
                  {rows.slice(0, 50).map((r) => {
                    let after;
                    if (mode === "absent") after = "ABSENT";
                    else if (mode === "common") after = `${firstIn} → ${commonLastOut || "?"}`;
                    else {
                      const orig = r.original_first_in || r.first_in || "?";
                      after = `${firstIn} → ${orig}`;
                    }
                    return (
                      <tr key={r.id}>
                        <td className="py-1">{r.employee_name}</td>
                        <td className="py-1">{ddmmyyyy(r.date)}</td>
                        <td className="py-1 font-mono text-slate-500">{r.original_first_in || r.first_in || "—"}</td>
                        <td className="py-1 font-mono text-emerald-700">{after}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.length > 50 && (
                <div className="text-[10px] text-slate-400 mt-1">…and {rows.length - 50} more rows</div>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100">Cancel</button>
          <button onClick={submit} disabled={busy || rows.length === 0} data-testid="v2-bulk-submit" className="px-4 py-2 rounded-md text-sm bg-bravura text-white hover:bg-bravura-700 font-bold disabled:opacity-50">
            {busy ? "Applying…" : `Apply to ${rows.length} ${rows.length === 1 ? "row" : "rows"}`}
          </button>
        </div>
      </div>
    </div>
  );
}


// ===========================================================================
// PayrollPreviewDialog — read-only preview of what the payroll engine will
// see when "Prepare Payroll" is clicked. Shows per-employee day_count /
// ot_count / late_ded / total_count BEFORE materialising into the legacy
// monthly summary collection.
// ===========================================================================
function PayrollPreviewDialog({ runId, month, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/attendance-v2/runs/${runId}/payroll-preview`,
        );
        setData(data);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
      } finally { setLoading(false); }
    })();
  }, [runId]);

  const rows = (data?.rows || []).filter((r) => {
    if (!search) return true;
    const t = search.toLowerCase();
    return (r.employee_code || "").toLowerCase().includes(t)
        || (r.employee_name || "").toLowerCase().includes(t);
  });

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="payroll-preview-dialog">
      <div className="bg-white w-full max-w-6xl max-h-[90vh] rounded-2xl flex flex-col"
           onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b flex items-center justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">
              Payroll Preview — {month}
            </div>
            <h2 className="text-lg font-bold text-slate-900">
              {data?.unit_name || "—"} · {data?.total_employees || 0} employees
            </h2>
          </div>
          <input value={search} onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search name/code…"
                 className="flex-1 max-w-xs rounded-md ring-1 ring-slate-300 px-3 py-1.5 text-sm" />
          <button onClick={onClose}
                  className="p-1.5 rounded-md hover:bg-slate-100">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-sm text-slate-500">Computing…</div>}
          {!loading && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600">
                  <tr>
                    <th className="px-2 py-2 text-left">Code</th>
                    <th className="px-2 py-2 text-left">Name</th>
                    <th className="px-2 py-2 text-center">Category</th>
                    <th className="px-2 py-2 text-center">Shift</th>
                    <th className="px-2 py-2 text-center">P/H/A</th>
                    <th className="px-2 py-2 text-center">Off</th>
                    <th className="px-2 py-2 text-right">Days</th>
                    <th className="px-2 py-2 text-right">OT count</th>
                    <th className="px-2 py-2 text-center">Late (G/H/Ded)</th>
                    <th className="px-2 py-2 text-right">EL/PL</th>
                    <th className="px-2 py-2 text-right bg-emerald-50">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.employee_id} className="border-t border-slate-100">
                      <td className="px-2 py-1.5 font-mono text-[11px]">
                        {r.employee_code}
                      </td>
                      <td className="px-2 py-1.5 font-semibold">{r.employee_name}</td>
                      <td className="px-2 py-1.5 text-center text-[10px]">
                        <code>{r.category}</code>
                      </td>
                      <td className="px-2 py-1.5 text-center text-[10px]">
                        {r.shift_hours}h<br />
                        <span className="text-slate-400">{r.shift_start_hhmm}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono text-[11px]">
                        {r.p}/{r.h}/<span className="text-red-600">{r.a}</span>
                      </td>
                      <td className="px-2 py-1.5 text-center">{r.off_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{r.day_count}</td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {r.ot_count}
                        <div className="text-[9px] text-slate-400">
                          ({Math.round(r.ot_minutes_total / 60 * 10) / 10}h)
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-center font-mono text-[10px]">
                        {r.grace_late}/{r.hard_late}/
                        <span className="text-red-700">{r.late_deduction_days}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right font-mono">
                        {r.used_el}/{r.used_pl}
                      </td>
                      <td className="px-2 py-1.5 text-right bg-emerald-50 font-bold text-emerald-700">
                        {r.total_count}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="p-3 border-t bg-slate-50 text-[11px] text-slate-600">
          <b>Formula:</b> total_count = day_count + ot_count − late_deduction + used_el + used_pl + off_count.
          Yeh single number payroll salary calculation drive karta hai.
        </div>
      </div>
    </div>
  );
}



// ----------------------------------------------------------------------
// Raw Grid Tab — per-employee × per-day grid with inline edit (super_admin
// can edit even after run is committed; HR locked once committed).
// ----------------------------------------------------------------------
const GRADE_COLOR = {
  P: "bg-emerald-50 text-emerald-700",
  H: "bg-amber-50 text-amber-800",
  A: "bg-red-50 text-red-700",
};
const minsToHHMM = (m) => {
  if (m == null || m === 0) return "00:00";
  const v = Math.abs(Math.round(m));
  const hh = Math.floor(v / 60);
  const mm = v % 60;
  return `${m < 0 ? "-" : ""}${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
};

function RawGridTab({ runId, status, isSuperAdmin, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // {emp, day}

  const isCommitted = status === "committed";
  const editLocked = isCommitted && !isSuperAdmin;

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/attendance-v2/runs/${runId}/daily-grid`);
      setData(d);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Grid load failed");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [runId]);

  if (loading && !data) {
    return <div className="text-sm text-muted-foreground p-6 text-center">Loading grid…</div>;
  }
  if (!data) return null;

  const filtered = (data.rows || []).filter((r) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.name || "").toLowerCase().includes(q)
      || (r.employee_code || "").toLowerCase().includes(q)
      || String(r.punch_id || "").toLowerCase().includes(q)
    );
  });

  const days = Array.from({ length: data.days_in_month }, (_, i) => i + 1);

  const onSaved = async () => {
    setEditing(null);
    await load();
    onChanged && onChanged();
  };

  return (
    <div className="space-y-3" data-testid="v2-raw-grid-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          className="px-3 py-1.5 border rounded-md text-sm flex-1 min-w-[200px]"
          placeholder="Search employee name / code / punch ID..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="grid-search"
        />
        <div className="text-xs text-muted-foreground">
          {filtered.length} employees · {data.days_in_month} days
        </div>
        <button
          onClick={load}
          className="px-2 py-1 rounded-md border text-xs hover:bg-slate-50"
          title="Reload"
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
      </div>

      {isCommitted && isSuperAdmin && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="v2-superadmin-banner">
          <b>Super-admin override mode</b> — Run committed hai par aap cell click karke time correct kar sakte hain. Sab changes audit log mein record honge.
        </div>
      )}

      <div className="border rounded-md overflow-auto" style={{ maxHeight: "75vh" }}>
        <table className="text-[11px] border-collapse" style={{ minWidth: "1400px" }}>
          <thead className="sticky top-0 bg-slate-50 z-10">
            <tr>
              <th className="sticky left-0 z-20 bg-slate-50 px-2 py-2 text-left border-r min-w-[180px]">Employee</th>
              <th className="px-2 py-2 text-left border-r">Field</th>
              {days.map((d) => (
                <th key={d} className="px-1.5 py-2 text-center border-r min-w-[44px]">{d}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((emp) => (
              <EmployeeGridBlock
                key={emp.employee_id}
                emp={emp}
                days={days}
                onCellClick={(d) => !editLocked && setEditing({ emp, day: d })}
                editLocked={editLocked}
              />
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={days.length + 2} className="p-6 text-center text-muted-foreground text-sm">
                  No employees matched search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="px-2 py-2 text-[10px] text-slate-500 flex flex-wrap gap-3">
        <span><span className="inline-block w-3 h-3 ring-2 ring-red-300 align-middle mr-1"></span>Anomaly</span>
        <span><span className="inline-block w-3 h-3 border-l-2 border-blue-400 align-middle mr-1"></span>Override</span>
        <span><span className="inline-block w-3 h-3 bg-slate-200 align-middle mr-1"></span>Off</span>
        <span><span className="inline-block w-3 h-3 bg-purple-50 align-middle mr-1"></span>Holiday</span>
        {!editLocked && <span className="text-emerald-700 ml-2">Click any cell to edit</span>}
      </div>

      {editing && (
        <EditDailyDialog
          emp={editing.emp}
          day={editing.day}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

function EmployeeGridBlock({ emp, days, onCellClick, editLocked }) {
  const dayMap = {};
  for (const d of emp.days || []) dayMap[d.day] = d;
  const ROWS = [
    { k: "first_in", lbl: "First In" },
    { k: "last_out", lbl: "Last Out" },
    { k: "total", lbl: "Total Hrs" },
    { k: "shift", lbl: "Shift" },
    { k: "ot", lbl: "OT" },
    { k: "grade", lbl: "Grade" },
  ];
  return (
    <>
      {ROWS.map((row, ri) => (
        <tr key={`${emp.employee_id}-${row.k}`} className={ri === 0 ? "border-t-2 border-slate-300" : ""}>
          {ri === 0 && (
            <td rowSpan={ROWS.length} className="sticky left-0 z-10 bg-white px-2 py-1 border-r border-b align-top min-w-[180px]">
              <div className="font-semibold">{emp.name}</div>
              <div className="text-[10px] text-slate-500">{emp.employee_code}</div>
              <div className="text-[10px] text-slate-500">
                {emp.category} · {emp.shift_hours}h
                <span className="ml-1 px-1 rounded bg-slate-100 text-[9px]">{emp.shift_source}</span>
              </div>
            </td>
          )}
          <td className="px-2 py-1 text-slate-600 border-r border-b text-[10px] uppercase tracking-wider whitespace-nowrap">
            {row.lbl}
          </td>
          {days.map((d) => {
            const day = dayMap[d];
            let display = "—";
            let cls = "";
            let title = "";
            if (day) {
              if (row.k === "first_in") display = day.first_in || "00:00";
              else if (row.k === "last_out") display = day.last_out || "00:00";
              else if (row.k === "total") display = minsToHHMM(day.worked_minutes);
              else if (row.k === "shift") display = emp.shift_hours;
              else if (row.k === "ot") {
                if (day.ot_minutes === 0) display = "—";
                else { display = minsToHHMM(day.ot_minutes); cls = day.ot_minutes > 0 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700"; }
              } else if (row.k === "grade") {
                display = day.grade || "—";
                cls = day.grade ? GRADE_COLOR[day.grade] || "" : "";
              }
              if (day.is_anomaly) cls += " ring-2 ring-red-300 ring-inset";
              if (day.is_override) cls += " border-l-2 border-blue-400";
              if (day.is_off) cls += " bg-slate-100 text-slate-400";
              if (day.is_holiday) cls = "bg-purple-50 " + cls;
              title = day.is_anomaly ? "Anomaly" : day.is_override ? "Override applied" : "";
            }
            return (
              <td
                key={d}
                onClick={() => day && onCellClick(d)}
                title={title}
                className={`px-1 py-1 text-center border-r border-b ${editLocked || !day ? "cursor-default" : "cursor-pointer hover:bg-blue-50"} ${cls}`}
                data-testid={`v2-grid-cell-${emp.employee_code || emp.employee_id}-${d}-${row.k}`}
              >
                {display}
              </td>
            );
          })}
        </tr>
      ))}
    </>
  );
}

function EditDailyDialog({ emp, day, onClose, onSaved }) {
  const d = (emp.days || []).find((x) => x.day === day) || {};
  const [firstIn, setFirstIn] = useState(d.first_in || "");
  const [lastOut, setLastOut] = useState(d.last_out || "");
  const [markAbsent, setMarkAbsent] = useState(false);
  const [isOff, setIsOff] = useState(d.is_off);
  const [isHol, setIsHol] = useState(d.is_holiday);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!reason.trim()) {
      toast.error("Reason required for audit trail");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/attendance-v2/daily/${d.daily_id}`, {
        first_in: markAbsent ? null : firstIn,
        last_out: markAbsent ? null : lastOut,
        mark_absent: markAbsent,
        is_off: isOff,
        is_holiday: isHol,
        reason,
      });
      toast.success(`Day ${day} updated`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" data-testid="v2-edit-dialog">
      <div className="bg-white rounded-lg w-full max-w-md p-4 space-y-3">
        <div className="flex justify-between items-start">
          <div>
            <div className="font-semibold flex items-center gap-1"><Pencil className="h-4 w-4" /> Override day {day}</div>
            <div className="text-xs text-slate-500">{emp.name} · {emp.employee_code}</div>
            <div className="text-xs text-slate-500">{d.date}</div>
          </div>
          <button onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-2 text-sm">
          <label className="space-y-1">
            <span className="text-xs text-slate-600">First In (HH:MM)</span>
            <input
              type="text" placeholder="09:00"
              value={firstIn} onChange={(e) => setFirstIn(e.target.value)}
              disabled={markAbsent}
              className="w-full px-2 py-1 border rounded-md text-sm"
              data-testid="edit-first-in"
            />
          </label>
          <label className="space-y-1">
            <span className="text-xs text-slate-600">Last Out (HH:MM)</span>
            <input
              type="text" placeholder="18:00"
              value={lastOut} onChange={(e) => setLastOut(e.target.value)}
              disabled={markAbsent}
              className="w-full px-2 py-1 border rounded-md text-sm"
              data-testid="edit-last-out"
            />
          </label>
        </div>

        <div className="flex items-center gap-3 text-sm">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={markAbsent} onChange={(e) => setMarkAbsent(e.target.checked)} data-testid="edit-mark-absent" />
            <span>Mark Absent</span>
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!isOff} onChange={(e) => setIsOff(e.target.checked)} />
            <span>Off</span>
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={!!isHol} onChange={(e) => setIsHol(e.target.checked)} />
            <span>Holiday</span>
          </label>
        </div>

        <label className="space-y-1 block">
          <span className="text-xs text-slate-600">Reason (audit trail) *</span>
          <textarea
            rows={2}
            placeholder="Forgot to punch / Sick leave / Holiday correction..."
            value={reason} onChange={(e) => setReason(e.target.value)}
            className="w-full px-2 py-1 border rounded-md text-sm"
            data-testid="edit-reason"
          />
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-3 py-1.5 rounded-md border text-sm">Cancel</button>
          <button
            onClick={save}
            disabled={busy || !reason.trim()}
            className="px-3 py-1.5 rounded-md bg-bravura text-white text-sm font-semibold disabled:opacity-50"
            data-testid="edit-save"
          >
            <Save className="h-3.5 w-3.5 inline mr-1" />
            {busy ? "Saving…" : "Save Override"}
          </button>
        </div>
      </div>
    </div>
  );
}
